/* MolCellQuest — SVG の viewBox を書き換えるピンチ／ホイール／慣性パン
   Svelte の action として使う：  <svg use:panZoom={{ vbW, vbH, focus }}>
   ライブラリ不要、Pointer Events だけで動く。 */

export function panZoom(svg, opts) {
  let base = { x: 0, y: 0, w: opts.vbW, h: opts.vbH };
  let vb = { ...base };
  let coverAR = base.w / base.h;
  let minW = base.w * 0.18; // 最大 5.5 倍まで寄れる
  let maxW = base.w;

  const pointers = new Map();
  let pinchPrev = 0;
  let vx = 0;
  let vy = 0;
  let lastMove = null;
  let inertiaId = null;

  const apply = () => {
    svg.setAttribute("viewBox", `${vb.x.toFixed(2)} ${vb.y.toFixed(2)} ${vb.w.toFixed(2)} ${vb.h.toFixed(2)}`);
  };

  const clampPan = () => {
    vb.x = Math.max(0, Math.min(base.w - vb.w, vb.x));
    vb.y = Math.max(0, Math.min(base.h - vb.h, vb.y));
  };

  function measure() {
    const r = svg.getBoundingClientRect();
    coverAR = r.width > 0 && r.height > 0 ? r.width / r.height : base.w / base.h;
    maxW = Math.min(base.w, base.h * coverAR);
    minW = base.w * 0.18;
  }

  /** 全体表示に戻す */
  function reset(focus) {
    measure();
    if (focus) {
      // 旅人を中心に、余白が出ない範囲で少し寄る
      let nw = Math.min(base.w, base.h * coverAR) * 0.82;
      let nh = nw / coverAR;
      if (nw > base.w) {
        nw = base.w;
        nh = nw / coverAR;
      }
      if (nh > base.h) {
        nh = base.h;
        nw = nh * coverAR;
      }
      vb = { x: focus.x - nw / 2, y: focus.y - nh / 2, w: nw, h: nh };
    } else {
      let nw = Math.min(base.w, base.h * coverAR);
      vb = { x: (base.w - nw) / 2, y: 0, w: nw, h: nw / coverAR };
      if (vb.h > base.h) {
        vb.h = base.h;
        vb.w = vb.h * coverAR;
        vb.x = (base.w - vb.w) / 2;
        vb.y = 0;
      }
    }
    clampPan();
    apply();
  }

  /** (px,py) を固定点にして拡大縮小する */
  function zoomAt(px, py, factor) {
    const nw = Math.max(minW, Math.min(maxW, vb.w * factor));
    const nh = nw / coverAR;
    vb.x = px - (px - vb.x) * (nw / vb.w);
    vb.y = py - (py - vb.y) * (nh / vb.h);
    vb.w = nw;
    vb.h = nh;
    clampPan();
    apply();
  }

  /** 画面座標 → viewBox 座標 */
  function toSvg(clientX, clientY) {
    const pt = svg.createSVGPoint ? svg.createSVGPoint() : null;
    const m = svg.getScreenCTM();
    if (!pt || !m) return { x: vb.x + vb.w / 2, y: vb.y + vb.h / 2 };
    pt.x = clientX;
    pt.y = clientY;
    return pt.matrixTransform(m.inverse());
  }

  function stopInertia() {
    if (inertiaId) {
      cancelAnimationFrame(inertiaId);
      inertiaId = null;
    }
  }

  function runInertia() {
    stopInertia();
    const step = () => {
      vx *= 0.95;
      vy *= 0.95;
      if (Math.abs(vx) < 0.1 && Math.abs(vy) < 0.1) {
        inertiaId = null;
        return;
      }
      vb.x -= vx * (vb.w / base.w);
      vb.y -= vy * (vb.h / base.h);
      clampPan();
      apply();
      inertiaId = requestAnimationFrame(step);
    };
    inertiaId = requestAnimationFrame(step);
  }

  /* ── イベント ─────────────────────────────────────── */

  function onWheel(e) {
    e.preventDefault();
    stopInertia();
    const p = toSvg(e.clientX, e.clientY);
    zoomAt(p.x, p.y, e.deltaY < 0 ? 0.82 : 1.22);
  }

  function onPointerDown(e) {
    stopInertia();
    svg.setPointerCapture(e.pointerId);
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY, t: performance.now() });
    if (pointers.size === 2) {
      const [a, b] = [...pointers.values()];
      pinchPrev = Math.hypot(a.x - b.x, a.y - b.y);
    }
    svg.classList.add("grabbing");
  }

  function onPointerMove(e) {
    const prev = pointers.get(e.pointerId);
    if (!prev) return;
    const now = performance.now();

    if (pointers.size >= 2) {
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY, t: now });
      const [a, b] = [...pointers.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      if (pinchPrev > 0 && dist > 0) {
        const mid = toSvg((a.x + b.x) / 2, (a.y + b.y) / 2);
        zoomAt(mid.x, mid.y, pinchPrev / dist);
      }
      pinchPrev = dist;
      return;
    }

    const dx = e.clientX - prev.x;
    const dy = e.clientY - prev.y;
    const dt = now - prev.t;
    // 一定時間より古い移動は速度に反映しない（指を止めてから離したら慣性なし）
    if (dt > 0 && dt < 100) {
      vx = (dx * 16.6) / dt;
      vy = (dy * 16.6) / dt;
    } else {
      vx = 0;
      vy = 0;
    }
    vb.x -= dx * (vb.w / svg.getBoundingClientRect().width);
    vb.y -= dy * (vb.h / svg.getBoundingClientRect().height);
    clampPan();
    apply();
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY, t: now });
    lastMove = now;
  }

  function onPointerUp(e) {
    pointers.delete(e.pointerId);
    if (pointers.size < 2) pinchPrev = 0;
    if (pointers.size === 0) {
      svg.classList.remove("grabbing");
      if (lastMove && performance.now() - lastMove < 120) runInertia();
    }
  }

  function onDblClick(e) {
    const p = toSvg(e.clientX, e.clientY);
    zoomAt(p.x, p.y, 0.6);
  }

  svg.style.touchAction = "none";
  svg.addEventListener("wheel", onWheel, { passive: false });
  svg.addEventListener("pointerdown", onPointerDown);
  svg.addEventListener("pointermove", onPointerMove);
  svg.addEventListener("pointerup", onPointerUp);
  svg.addEventListener("pointercancel", onPointerUp);
  svg.addEventListener("dblclick", onDblClick);

  const onResize = () => {
    measure();
    clampPan();
    apply();
  };
  window.addEventListener("resize", onResize);

  reset(opts.focus);

  // 外から操作するためのハンドルを要素に生やしておく
  svg.__zoom = {
    in: () => zoomAt(vb.x + vb.w / 2, vb.y + vb.h / 2, 0.7),
    out: () => zoomAt(vb.x + vb.w / 2, vb.y + vb.h / 2, 1.4),
    reset: () => reset(null),
    focusOn: (pt) => reset(pt),
  };

  return {
    update(next) {
      // 別の時代に切り替わったら基準を作り直す
      if (next.vbW !== base.w || next.vbH !== base.h || next.sceneId !== opts.sceneId) {
        base = { x: 0, y: 0, w: next.vbW, h: next.vbH };
        opts = next;
        reset(next.focus);
      }
    },
    destroy() {
      stopInertia();
      window.removeEventListener("resize", onResize);
      svg.removeEventListener("wheel", onWheel);
      svg.removeEventListener("pointerdown", onPointerDown);
      svg.removeEventListener("pointermove", onPointerMove);
      svg.removeEventListener("pointerup", onPointerUp);
      svg.removeEventListener("pointercancel", onPointerUp);
      svg.removeEventListener("dblclick", onDblClick);
      delete svg.__zoom;
    },
  };
}
